import { useEffect, useState } from "react";
import { Sliders, Save, AlertTriangle, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PRIORITY_STY = {
  critical: { bg: "bg-rose-100",    text: "text-rose-800",    border: "border-rose-200" },
  high:     { bg: "bg-amber-100",   text: "text-amber-900",   border: "border-amber-200" },
  medium:   { bg: "bg-blue-100",    text: "text-blue-900",    border: "border-blue-200" },
  low:      { bg: "bg-slate-100",   text: "text-slate-700",   border: "border-slate-200" },
};

export default function AdminRecommendations() {
  const [data, setData] = useState({ rules: [], categories: [], priorities: [] });
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState({});
  const [showDisabled, setShowDisabled] = useState(true);

  const load = async () => {
    const { data } = await api.get("/admin/recommendations/rules");
    setData(data);
    // Hydrate editing buffer
    const ed = {};
    data.rules.forEach((r) => {
      ed[r.key] = {
        threshold: r.threshold, priority: r.priority, category: r.category,
        title: r.title, body: r.body, suggested_actions: (r.suggested_actions || []).join("\n"),
      };
    });
    setEditing(ed);
  };
  useEffect(() => { load(); }, []);

  const toggleEnabled = async (rule) => {
    setSaving({ ...saving, [rule.key]: true });
    try {
      await api.post(`/admin/recommendations/rules/${rule.key}`, { enabled: !rule.enabled });
      toast.success(`${rule.title} ${!rule.enabled ? "enabled" : "disabled"}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update rule");
    } finally {
      setSaving({ ...saving, [rule.key]: false });
    }
  };

  const saveRule = async (rule) => {
    const ed = editing[rule.key];
    if (!ed) return;
    setSaving({ ...saving, [rule.key]: true });
    try {
      const payload = {
        threshold: rule.param === "__always" ? rule.threshold : Number(ed.threshold),
        priority: ed.priority, category: ed.category,
        title: ed.title, body: ed.body,
        suggested_actions: ed.suggested_actions.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      await api.post(`/admin/recommendations/rules/${rule.key}`, payload);
      toast.success("Rule saved · new version recorded");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving({ ...saving, [rule.key]: false });
    }
  };

  const visibleRules = data.rules.filter((r) => showDisabled || r.enabled);

  return (
    <PortalShell title="Recommendation engine" subtitle="Admin · Water quality rules">
      <div className="mb-5 rounded-2xl bg-slate-900 text-white p-5 flex items-start gap-4">
        <Sliders className="w-5 h-5 text-cyan-400 mt-0.5" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">Rules engine</div>
          <div className="text-sm mt-1">{data.rules.length} rules across {data.categories.length} categories</div>
          <p className="text-xs text-slate-300 mt-1.5">Each rule runs against the latest published test results. Customers see all triggered rules sorted by priority. Every edit is version-controlled (full history saved on the rule).</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-300">Show disabled</span>
          <Switch data-testid="rec-show-disabled" checked={showDisabled} onCheckedChange={setShowDisabled} />
        </div>
      </div>

      <div className="space-y-4">
        {visibleRules.map((r) => {
          const ed = editing[r.key] || {};
          const sty = PRIORITY_STY[ed.priority || r.priority] || PRIORITY_STY.medium;
          return (
            <div key={r.key} data-testid={`rec-rule-${r.key}`} className={`rounded-3xl bg-white border ${r.enabled ? "border-slate-200" : "border-slate-200 opacity-70"} p-5`}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="px-2 py-0.5 rounded bg-slate-100 text-[10px] font-mono text-slate-600">{r.key}</code>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${sty.bg} ${sty.text}`}>{ed.priority || r.priority}</span>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{(ed.category || r.category).replace(/_/g, " ")}</span>
                    <span className="text-[10px] text-slate-400">v{r.version || 1}</span>
                  </div>
                  <h3 className="font-display text-lg font-semibold text-slate-900 mt-1.5">{ed.title || r.title}</h3>
                  <div className="text-xs text-slate-500 mt-1">Triggers when <code className="bg-slate-100 px-1 rounded">{r.param}</code> {r.op} <code className="bg-slate-100 px-1 rounded">{ed.threshold ?? r.threshold}</code></div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">{r.enabled ? "Enabled" : "Disabled"}</span>
                  <Switch data-testid={`rec-toggle-${r.key}`} checked={r.enabled} onCheckedChange={() => toggleEnabled(r)} disabled={saving[r.key]} />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Threshold</Label>
                  <Input data-testid={`rec-threshold-${r.key}`} type="number" step="any" disabled={r.param === "__always"} value={ed.threshold ?? r.threshold} onChange={(e) => setEditing({ ...editing, [r.key]: { ...ed, threshold: e.target.value } })} className="mt-1.5 h-10 rounded-xl" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Priority</Label>
                  <Select value={ed.priority || r.priority} onValueChange={(v) => setEditing({ ...editing, [r.key]: { ...ed, priority: v } })}>
                    <SelectTrigger data-testid={`rec-priority-${r.key}`} className="mt-1.5 h-10 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{data.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Category</Label>
                  <Select value={ed.category || r.category} onValueChange={(v) => setEditing({ ...editing, [r.key]: { ...ed, category: v } })}>
                    <SelectTrigger data-testid={`rec-category-${r.key}`} className="mt-1.5 h-10 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{data.categories.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-3">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Title (customer-facing)</Label>
                  <Input data-testid={`rec-title-${r.key}`} value={ed.title ?? r.title} onChange={(e) => setEditing({ ...editing, [r.key]: { ...ed, title: e.target.value } })} className="mt-1.5 h-10 rounded-xl" />
                </div>
                <div className="md:col-span-3">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Plain-English body</Label>
                  <Textarea data-testid={`rec-body-${r.key}`} value={ed.body ?? r.body} onChange={(e) => setEditing({ ...editing, [r.key]: { ...ed, body: e.target.value } })} className="mt-1.5 rounded-xl" rows={2} />
                </div>
                <div className="md:col-span-3">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Suggested actions (one per line)</Label>
                  <Textarea data-testid={`rec-actions-${r.key}`} value={ed.suggested_actions ?? (r.suggested_actions || []).join("\n")} onChange={(e) => setEditing({ ...editing, [r.key]: { ...ed, suggested_actions: e.target.value } })} className="mt-1.5 rounded-xl font-mono text-xs" rows={4} />
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                  {r.enabled ? <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> : <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />}
                  {r.version_history?.length ? `${r.version_history.length} prior version${r.version_history.length === 1 ? "" : "s"} archived` : "No prior edits"}
                </div>
                <Button data-testid={`rec-save-${r.key}`} onClick={() => saveRule(r)} disabled={saving[r.key]} className="btn-aqua rounded-full font-semibold">
                  <Save className="w-4 h-4 mr-1.5" /> Save rule
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </PortalShell>
  );
}
